import clsx from "clsx";
import { twMerge } from "tailwind-merge";
import { forwardRef } from "react";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "light"
  | "dark"
  | "danger"
  | "warning";

export type ButtonMode = "raised" | "flat";
export type ButtonSize = "sm" | "lg" | "xl";

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  mode?: ButtonMode;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const {
    className,
    type = "button",
    variant = "primary",
    size = "lg",
    mode = "raised",
    ...rest
  } = props;
  return (
    <button
      {...rest}
      type={type}
      ref={ref}
      className={twMerge(
        clsx(
          "enabled:cursor-pointer select-none",
          "disabled:opacity-75",
          "font-medium",
          "transition-all duration-300",
          {
            "enabled:shadow-md enabled:hover:shadow-lg": mode === "raised",
            "bg-primary text-white enabled:hover:bg-hunter-400":
              variant === "primary",
            "bg-primary-light text-primary-dark enabled:hover:bg-hunter-200":
              variant === "secondary",
            "bg-gunmetal-50 text-gunmetal-900 enabled:hover:bg-gunmetal-200":
              variant === "light",
            "bg-gunmetal-700 text-white enabled:hover:bg-gunmetal-500":
              variant === "dark",
            "bg-red-500 text-white enabled:hover:bg-red-400":
              variant === "danger",
            "bg-yellow-500 text-white enabled:hover:bg-yellow-400":
              variant === "warning",
            "rounded-xl px-4 py-3": size === "xl",
            "rounded-lg px-3 py-1.5": size === "lg",
            "rounded-md px-2 py-1": size === "sm",
          }
        ),
        className
      )}
    />
  );
});

export default Button;
